/*
 * Write-review adapter for the photo-first reviews panel.
 *
 * Trustoo's app renders the form; this re-skins it in place from inside a
 * MutationObserver, which means anything it changes can wake it up again.
 * Two render loops have already come out of that. Before pushing any change
 * here, load a product page and measure: mutations per second and frames per
 * second. Roughly 270 mutations in a 1.5s sample is the theme's own baseline;
 * over about 500 means this file is looping.
 */
(() => {
  'use strict';

  const V2_SELECTOR = '.pr-section--photo_first_v2';
  const FORM_SELECTOR = '.trustoo-write-review';
  const MASK_SELECTOR = '#trustoo-mask';
  const SUBMIT_SELECTOR = [
    'button[type="submit"]',
    'input[type="submit"]',
    'button[class*="submit" i]',
    'input[class*="submit" i]',
    'a[class*="submit" i]'
  ].join(',');
  const CLOSE_SELECTOR = [
    'button[class*="close" i]',
    '[class*="window-close" i]',
    '[aria-label*="close" i]',
    '[title*="close" i]'
  ].join(',');
  const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
  const RATING_LABELS = ['Select rating', 'Poor', 'Fair', 'Good', 'Very good', 'Excellent'];
  // Exact Phosphor icon paths used by the Aykah theme snippets.
  const ICON_PATHS = {
    x: 'M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z',
    camera: 'M208,56H180.28L166.65,35.56A8,8,0,0,0,160,32H96a8,8,0,0,0-6.65,3.56L75.71,56H48A24,24,0,0,0,24,80V192a24,24,0,0,0,24,24H208a24,24,0,0,0,24-24V80A24,24,0,0,0,208,56Zm8,136a8,8,0,0,1-8,8H48a8,8,0,0,1-8-8V80a8,8,0,0,1,8-8H80a8,8,0,0,0,6.66-3.56L100.28,48h55.43l13.63,20.44A8,8,0,0,0,176,72h32a8,8,0,0,1,8,8ZM128,88a44,44,0,1,0,44,44A44.05,44.05,0,0,0,128,88Zm0,72a28,28,0,1,1,28-28A28,28,0,0,1,128,160Z',
    checkCircle: 'M173.66,98.34a8,8,0,0,1,0,11.32l-56,56a8,8,0,0,1-11.32,0l-24-24a8,8,0,0,1,11.32-11.32L112,148.69l50.34-50.35A8,8,0,0,1,173.66,98.34ZM232,128A104,104,0,1,1,128,24,104.11,104.11,0,0,1,232,128Zm-16,0a88,88,0,1,0-88,88A88.1,88.1,0,0,0,216,128Z',
    // Same star the review cards use, so the form and the panel match.
    star: 'M234.29,114.85l-45,38.83L203,211.75a16.4,16.4,0,0,1-24.5,17.82L128,198.49,77.47,229.57A16.4,16.4,0,0,1,53,211.75l13.76-58.07-45-38.83A16.46,16.46,0,0,1,31.08,86l59-4.76,22.76-55.08a16.36,16.36,0,0,1,30.27,0l22.75,55.08,59,4.76a16.46,16.46,0,0,1,9.37,28.86Z',
    plus: 'M224,128a8,8,0,0,1-8,8H136v80a8,8,0,0,1-16,0V136H40a8,8,0,0,1,0-16h80V40a8,8,0,0,1,16,0v80h80A8,8,0,0,1,224,128Z',
    warningCircle: 'M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm-8,56a8,8,0,0,1,16,0v56a8,8,0,0,1-16,0Zm8,104a12,12,0,1,1,12-12A12,12,0,0,1,128,184Z',
    check: 'M205.66,85.66l-96,96a8,8,0,0,1-11.32,0l-40-40A8,8,0,0,1,69.66,130.34L104,164.69l90.34-90.35a8,8,0,0,1,11.32,11.32Z',
    play: 'M232.4,114.49,88.32,26.35a16,16,0,0,0-16.2-.3A15.86,15.86,0,0,0,64,39.87V216.13A15.94,15.94,0,0,0,80,232a16.07,16.07,0,0,0,8.36-2.35L232.4,141.51a15.81,15.81,0,0,0,0-27ZM80,215.94V40l143.83,88Z'
  };

  const createElement = (tagName, className, textContent = '') => {
    const element = document.createElement(tagName);
    element.className = className;
    if (textContent) element.textContent = textContent;
    return element;
  };

  const makeIcon = (name, className) => {
    const svg = document.createElementNS(SVG_NAMESPACE, 'svg');
    const path = document.createElementNS(SVG_NAMESPACE, 'path');
    svg.setAttribute('viewBox', '0 0 256 256');
    svg.setAttribute('fill', 'currentColor');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    svg.classList.add('aykah-trustoo-icon', className);
    path.setAttribute('d', ICON_PATHS[name]);
    svg.appendChild(path);
    return svg;
  };

  const ensureIcon = (host, name, className, position = 'afterbegin') => {
    if (!host || host.querySelector(`.${className}`)) return;
    host.insertAdjacentElement(position, makeIcon(name, className));
  };

  const attributeText = (control) => [
    control.name,
    control.id,
    control.placeholder,
    control.autocomplete,
    control.getAttribute('aria-label'),
    Array.from(control.labels || []).map((label) => label.textContent).join(' ')
  ].filter(Boolean).join(' ').toLowerCase();

  const findInput = (form, type, hints) => {
    const controls = Array.from(form.querySelectorAll('input:not([type="hidden"]):not([type="submit"]):not([type="button"]):not([type="file"]):not([type="radio"]):not([type="checkbox"])'));
    const exactType = controls.find((control) => control.type === type);
    if (exactType && type !== 'text') return exactType;
    const hinted = controls.find((control) => hints.some((hint) => attributeText(control).includes(hint)));
    if (hinted) return hinted;
    return type === 'text' ? controls.find((control) => control.type === 'text' || !control.type) || null : exactType || null;
  };

  const findRatingRoot = (form) => {
    const explicit = form.querySelector([
      '[role="radiogroup"]',
      '[class*="rating-stars" i]',
      '[class*="star-list" i]',
      '[class*="score-list" i]'
    ].join(','));
    if (explicit) return explicit;

    const ratingInput = form.querySelector([
      'input[type="radio"][name*="rating" i]',
      'input[type="radio"][name*="rate" i]',
      'input[type="radio"][name*="star" i]'
    ].join(','));
    if (ratingInput) {
      return ratingInput.closest('fieldset, [class*="rating" i], [class*="star" i]') || ratingInput.parentElement;
    }

    const stars = Array.from(form.querySelectorAll('.star-item'));
    if (!stars.length) return null;
    let root = stars[0].parentElement;
    while (root?.parentElement && root.parentElement !== form && stars.every((star) => root.parentElement.contains(star))) {
      const parent = root.parentElement;
      if (parent.querySelector('textarea, input[type="email"]')) break;
      root = parent;
    }
    return root;
  };

  const findUploadRoot = (form) => {
    const uploadArea = form.querySelector('.upload-area');
    if (uploadArea) return uploadArea;
    const fileInput = form.querySelector('input[type="file"]');
    if (!fileInput) return null;
    return fileInput.closest('[class*="upload" i], label, [class*="field" i], [class*="item" i]') || fileInput.parentElement;
  };

  const findFieldGroup = (control, form, otherControls = []) => {
    if (!control || control === form) return control;
    let current = control;
    let best = control;
    while (current.parentElement && current.parentElement !== form) {
      const parent = current.parentElement;
      if (otherControls.some((other) => other && other !== control && parent.contains(other))) break;
      best = parent;
      current = parent;
    }
    return best;
  };

  const markPathAsContents = (node, form) => {
    let current = node?.parentElement;
    while (current && current !== form) {
      current.classList.add('aykah-trustoo-pass-through');
      current = current.parentElement;
    }
  };

  const addFieldHint = (form, modifier, text) => {
    const existing = form.querySelector(`.aykah-trustoo-hint--${modifier}`);
    if (existing) return existing;
    const hint = createElement('span', `aykah-trustoo-field-hint aykah-trustoo-hint--${modifier}`, text);
    hint.setAttribute('aria-hidden', 'true');
    form.appendChild(hint);
    return hint;
  };

  const addFieldLabel = (form, modifier, text) => {
    const existing = form.querySelector(`.aykah-trustoo-label--${modifier}`);
    if (existing) return existing;
    const label = createElement('span', `aykah-trustoo-field-label aykah-trustoo-label--${modifier}`, text);
    label.setAttribute('aria-hidden', 'true');
    form.appendChild(label);
    return label;
  };

  const addProductSummary = (form, section) => {
    let summary = form.querySelector('.aykah-trustoo-product');
    if (summary) return summary;

    summary = createElement('div', 'aykah-trustoo-product');
    const thumb = createElement('span', 'aykah-trustoo-product-thumb');
    const imageUrl = section?.dataset.prProductImage || '';
    if (imageUrl) {
      const image = document.createElement('img');
      image.src = imageUrl;
      image.alt = '';
      image.loading = 'eager';
      thumb.appendChild(image);
    } else {
      thumb.appendChild(createElement('span', 'aykah-trustoo-product-placeholder'));
    }

    const copy = createElement('span', 'aykah-trustoo-product-copy');
    copy.appendChild(createElement('strong', 'aykah-trustoo-product-title', section?.dataset.prProductTitle || 'Your product'));
    const variant = createElement('span', 'aykah-trustoo-product-variant', section?.dataset.prProductVariant || '');
    variant.hidden = !variant.textContent;
    copy.appendChild(variant);
    const verified = createElement('span', 'aykah-trustoo-verified', section?.dataset.prVerifiedText || 'Verified buyer');
    verified.insertAdjacentElement('afterbegin', makeIcon('check', 'aykah-trustoo-verified-icon'));
    copy.appendChild(verified);

    summary.append(thumb, copy);
    form.appendChild(summary);
    return summary;
  };

  const readRating = (ratingRoot) => {
    if (!ratingRoot) return 0;
    const checked = ratingRoot.querySelector('input[type="radio"]:checked');
    if (checked) return Math.max(0, Math.min(5, Number(checked.value) || 0));

    const selected = ratingRoot.querySelector('[aria-checked="true"], [aria-pressed="true"], .is-active, .active, .selected');
    if (selected) {
      const value = Number(selected.dataset.value || selected.dataset.rating || selected.getAttribute('aria-label')?.match(/[1-5]/)?.[0]);
      if (value) return Math.max(1, Math.min(5, value));
    }
    return 0;
  };

  const STAR_ITEM_SELECTOR = '.star-item, [role="radio"]';

  const starItems = (ratingRoot) => Array.from(ratingRoot?.querySelectorAll(STAR_ITEM_SELECTOR) || []);

  // Trustoo draws a sharp-cornered star and marks the filled ones by swapping
  // its own SVG contents, so rewriting those paths breaks its display. Add the
  // Aykah star alongside, hide theirs in CSS, and drive the fill from here.
  const restyleStars = (ratingRoot) => {
    starItems(ratingRoot).forEach((item) => {
      if (item.querySelector('.aykah-trustoo-star-icon')) return;
      item.appendChild(makeIcon('star', 'aykah-trustoo-star-icon'));
    });
  };

  const paintStars = (ratingRoot, value) => {
    starItems(ratingRoot).forEach((item, index) => {
      item.classList.toggle('aykah-trustoo-star-on', index < value);
    });
  };

  // Field-level messages say what is wrong once you find them. The count says how
  // many there are before you start looking. Only ever shown after a submit press.
  const ERROR_SOURCES = [
    ['.feedback-tip', 'your review'],
    ['.name-tip', 'your name'],
    ['.email-tip', 'the email address']
  ];

  const joinNames = (names) => (
    names.length > 1
      ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
      : names[0]
  );

  const installErrorSummary = (form, ratingRoot) => {
    let card = form.querySelector('.aykah-trustoo-error-summary');
    if (!card) {
      card = createElement('div', 'aykah-trustoo-error-summary');
      card.setAttribute('role', 'status');
      const dot = createElement('span', 'aykah-trustoo-error-dot');
      dot.appendChild(makeIcon('warningCircle', 'aykah-trustoo-error-icon'));
      card.appendChild(dot);
      card.appendChild(createElement('strong', 'aykah-trustoo-error-title'));
      card.appendChild(createElement('span', 'aykah-trustoo-error-detail'));
      form.appendChild(card);
    }
    if (card.dataset.aykahBound === 'true') return card;
    card.dataset.aykahBound = 'true';

    const title = card.querySelector('.aykah-trustoo-error-title');
    const detail = card.querySelector('.aykah-trustoo-error-detail');
    const setText = (node, value) => {
      if (node.textContent !== value) node.textContent = value;
    };

    // Read the state off the DOM rather than remembering a click. Trustoo
    // re-renders the form on validation, so anything this closure remembers is
    // gone by the time the errors actually appear.
    const refresh = () => {
      const tips = ERROR_SOURCES.filter(([selector]) => form.querySelector(`.error-tip${selector}.display`));
      const names = tips.map(([, label]) => label);
      if (names.length && ratingRoot && readRating(ratingRoot) === 0) names.unshift('your rating');
      const show = names.length > 0;
      if (show) {
        setText(title, names.length === 1 ? '1 thing to fix' : `${names.length} things to fix`);
        const listed = joinNames(names);
        setText(detail, `${listed.charAt(0).toUpperCase()}${listed.slice(1)}.`);
      }
      if (card.classList.contains('is-visible') !== show) card.classList.toggle('is-visible', show);
    };

    const submit = form.querySelector(SUBMIT_SELECTOR) || form.closest('.trustoo-write-review')?.querySelector(SUBMIT_SELECTOR);
    if (submit) {
      submit.addEventListener('click', () => {
        // Captured now because the form is gone by the time the panel appears.
        const nameField = form.querySelector('.name-area input') || findInput(form, 'text', ['name', 'author', 'nickname']);
        const value = (nameField?.value || '').trim();
        if (value) lastReviewerName = value.split(/\s+/)[0];
        window.setTimeout(refresh, 200);
      });
    }
    const observer = new MutationObserver(() => window.requestAnimationFrame(refresh));
    observer.observe(form, { subtree: true, attributes: true, attributeFilter: ['class'] });
    form.addEventListener('input', () => window.requestAnimationFrame(refresh));
    return card;
  };

  const installRatingCopy = (form, ratingRoot) => {
    let copy = form.querySelector('.aykah-trustoo-rating-copy');
    if (!copy) {
      copy = createElement('span', 'aykah-trustoo-rating-copy', RATING_LABELS[readRating(ratingRoot)]);
      copy.setAttribute('aria-live', 'polite');
      form.appendChild(copy);
    }

    if (!ratingRoot || ratingRoot.dataset.aykahRatingBound === 'true') return;
    ratingRoot.dataset.aykahRatingBound = 'true';
    const update = (event) => {
      const stars = Array.from(ratingRoot.querySelectorAll('.star-item, [role="radio"], input[type="radio"]'));
      const target = event?.target?.closest?.('.star-item, [role="radio"], label, input[type="radio"]');
      const nestedInput = target?.querySelector?.('input[type="radio"]');
      let value = Number(target?.dataset?.value || target?.dataset?.rating || target?.value || nestedInput?.value || 0);
      if (!value && target && stars.length) {
        value = stars.findIndex((star) => star === target || star.contains(target) || target.contains(star)) + 1;
      }
      if (!value) value = readRating(ratingRoot);
      value = Math.max(0, Math.min(5, value));
      if (event) ratingRoot.dataset.aykahRating = String(value);
      copy.textContent = RATING_LABELS[value];
      paintStars(ratingRoot, value);
    };
    ratingRoot.addEventListener('click', update);
    ratingRoot.addEventListener('change', update);

    // Hover previews the score, then falls back to whatever is chosen.
    ratingRoot.addEventListener('mouseover', (event) => {
      const target = event.target?.closest?.(STAR_ITEM_SELECTOR);
      if (!target) return;
      paintStars(ratingRoot, starItems(ratingRoot).indexOf(target) + 1);
    });
    ratingRoot.addEventListener('mouseleave', () => {
      paintStars(ratingRoot, Number(ratingRoot.dataset.aykahRating) || 0);
    });

    update();
  };

  const installCounter = (form, textarea) => {
    let counter = form.querySelector('.aykah-trustoo-counter');
    if (!counter) {
      counter = createElement('span', 'aykah-trustoo-counter');
      counter.setAttribute('aria-live', 'polite');
      form.appendChild(counter);
    }
    const maximum = Number(textarea.maxLength) > 0 ? Number(textarea.maxLength) : 2000;
    if (textarea.maxLength < 0) textarea.maxLength = maximum;
    const update = () => {
      const value = `${textarea.value.length} / ${maximum}`;
      if (counter.textContent !== value) counter.textContent = value;
    };
    if (textarea.dataset.aykahCounterBound !== 'true') {
      textarea.dataset.aykahCounterBound = 'true';
      textarea.addEventListener('input', update);
    }
    update();
  };

  const decorateUploadTile = (tile, iconName, label) => {
    tile.classList.add('aykah-trustoo-upload-area');
    ensureIcon(tile, iconName, 'aykah-trustoo-camera-icon');
    if (!tile.querySelector('.aykah-trustoo-upload-label')) {
      tile.appendChild(createElement('span', 'aykah-trustoo-upload-label', label));
    }
  };

  /*
   * One control, not two. Both of Trustoo's uploaders accept the same thing -
   * their accept lists are identical and both cover images and video - so the
   * Photo/Video split was asking people to make a choice that does not exist.
   * The second uploader stays in the DOM because Trustoo's own code holds a
   * reference to it; it is only taken out of the layout.
   */
  const collapseUploaders = (form) => {
    const uploaders = Array.from(form.querySelectorAll('.tt-uploader'));
    if (uploaders.length < 2) return;
    uploaders.slice(1).forEach((node) => node.classList.add('aykah-trustoo-uploader-hidden'));
    const primary = uploaders[0];
    primary.classList.add('aykah-trustoo-uploader-primary');
    // Hidden by CSS, never removed. decorateUpload re-adds these on every pass,
    // so removing them here made the two fight and re-triggered the observer
    // on every frame.
    if (!primary.querySelector('.aykah-trustoo-plus-icon')) {
      primary.insertAdjacentElement('afterbegin', makeIcon('plus', 'aykah-trustoo-plus-icon'));
    }

  };

  const decorateUpload = (form, uploadRoot) => {
    if (!uploadRoot) return;
    uploadRoot.classList.add('aykah-trustoo-upload-row');

    // Trustoo gives photos and video their own uploader. Both are real tiles;
    // the decorative slots only fill out the row.
    const tiles = Array.from(uploadRoot.querySelectorAll('.tt-uploader'))
      .filter((tile) => !tile.classList.contains('aykah-trustoo-uploader-hidden'));
    if (tiles.length) {
      tiles.forEach((tile) => {
        const isVideo = tile.classList.contains('tt-video-uploader')
          || /video/i.test(tile.textContent);
        decorateUploadTile(tile, isVideo ? 'play' : 'camera', isVideo ? 'Video' : 'Photo');
      });
    } else {
      decorateUploadTile(uploadRoot, 'camera', 'Add');
    }

    const slotCount = Math.max(0, 4 - (tiles.length || 1));
    let slots = form.querySelector('.aykah-trustoo-upload-slots');
    if (!slots) {
      slots = createElement('div', 'aykah-trustoo-upload-slots');
      form.appendChild(slots);
    }
    while (slots.children.length > slotCount) slots.lastElementChild.remove();
    while (slots.children.length < slotCount) {
      slots.appendChild(createElement('span', 'aykah-trustoo-upload-slot'));
    }
    const tileCount = String(tiles.length || 1);
    if (slots.dataset.aykahTiles !== tileCount) slots.dataset.aykahTiles = tileCount;

    if (!form.querySelector('.aykah-trustoo-upload-help')) {
      form.appendChild(createElement(
        'p',
        'aykah-trustoo-upload-help',
        'Up to 5 photos and one video. A room shot helps more than a close-up.'
      ));
    }
  };

  const decorateNativeCopy = (root) => {
    root.querySelectorAll('h1, h2, h3').forEach((heading) => {
      if (!heading.classList.contains('aykah-trustoo-title')) heading.classList.add('aykah-trustoo-native-copy');
    });
    root.querySelectorAll('p, span, div, label').forEach((element) => {
      if (element.closest([
        '.aykah-trustoo-title',
        '.aykah-trustoo-product',
        '.aykah-trustoo-field',
        '.aykah-trustoo-field-label',
        '.aykah-trustoo-field-hint',
        '.aykah-trustoo-error-summary',
        '.aykah-trustoo-success-panel',
        '.aykah-trustoo-rating-copy',
        '.aykah-trustoo-upload-slots',
        '.aykah-trustoo-upload-help',
        '.aykah-trustoo-counter',
        '.aykah-trustoo-privacy',
        '.aykah-trustoo-close',
        '.aykah-trustoo-submit',
        '.aykah-trustoo-success',
        '[class*="error" i]',
        '[role="alert"]'
      ].join(','))) return;
      if (element.querySelector('input, textarea, select, button, svg, img, [role="radio"]')) return;
      if (element.textContent.trim()) element.classList.add('aykah-trustoo-native-copy');
    });
  };

  // Trustoo renders its submit button as a sibling of the inner <form>, not
  // inside it, so the first <form> is never the element that owns the layout.
  // The host is the closest ancestor of the textarea that also holds the submit.
  const findLayoutHost = (root, textarea, submit) => {
    let host = textarea.parentElement;
    while (host && host !== root && !host.contains(submit)) host = host.parentElement;
    return host || root;
  };

  const installLayout = (root, section) => {
    const textarea = root.querySelector('textarea');
    const submit = root.querySelector(SUBMIT_SELECTOR);
    if (!textarea || !submit) return false;
    const nativeForm = findLayoutHost(root, textarea, submit);

    nativeForm.classList.add('aykah-trustoo-native-form');
    root.classList.toggle('aykah-trustoo-form-is-root', nativeForm === root);

    let title = root.querySelector('.aykah-trustoo-title');
    if (!title) {
      title = createElement('h2', 'aykah-trustoo-title', 'Write a review');
      root.insertAdjacentElement('afterbegin', title);
    }

    const nameInput = findInput(nativeForm, 'text', ['name', 'author', 'nickname']);
    const emailInput = findInput(nativeForm, 'email', ['email', 'e-mail']);
    const ratingRoot = findRatingRoot(nativeForm);
    const uploadRoot = findUploadRoot(nativeForm);
    const coreControls = [textarea, nameInput, emailInput, ratingRoot, uploadRoot, submit].filter(Boolean);

    const reviewGroup = findFieldGroup(textarea, nativeForm, coreControls);
    const nameGroup = findFieldGroup(nameInput, nativeForm, coreControls);
    const emailGroup = findFieldGroup(emailInput, nativeForm, coreControls);

    [
      [ratingRoot, 'rating'],
      [uploadRoot, 'upload'],
      [reviewGroup, 'review'],
      [nameGroup, 'name'],
      [emailGroup, 'email']
    ].forEach(([group, modifier]) => {
      if (!group) return;
      group.classList.add('aykah-trustoo-field', `aykah-trustoo-field--${modifier}`);
      markPathAsContents(group, nativeForm);
    });

    submit.classList.add('aykah-trustoo-submit');
    markPathAsContents(submit, nativeForm);

    addProductSummary(nativeForm, section);
    if (ratingRoot) addFieldLabel(nativeForm, 'rating', 'YOUR RATING');
    if (uploadRoot) addFieldLabel(nativeForm, 'photos', 'PHOTOS');
    addFieldLabel(nativeForm, 'review', 'YOUR REVIEW');
    if (nameInput) addFieldLabel(nativeForm, 'name', 'YOUR NAME');
    if (emailInput) addFieldLabel(nativeForm, 'email', 'EMAIL');

    // The textarea keeps its prompt - it is a writing prompt, not a label.
    // Name and email hand their hints to the label row so the field reads empty.
    const prompt = 'What did you think? Colour, comfort, how it looks in your room.';
    if (textarea.placeholder !== prompt) textarea.placeholder = prompt;
    if (nameInput) {
      if (nameInput.placeholder) nameInput.placeholder = '';
      addFieldHint(nativeForm, 'name', 'Shown as Sarah M.');
    }
    if (emailInput) {
      if (emailInput.placeholder) emailInput.placeholder = '';
      addFieldHint(nativeForm, 'email', 'Never published');
    }

    restyleStars(ratingRoot);
    installRatingCopy(nativeForm, ratingRoot);
    installErrorSummary(nativeForm, ratingRoot);
    installCounter(nativeForm, textarea);
    collapseUploaders(nativeForm);
    decorateUpload(nativeForm, uploadRoot);

    if (!nativeForm.querySelector('.aykah-trustoo-privacy')) {
      const privacy = createElement('p', 'aykah-trustoo-privacy');
      privacy.append('By submitting you agree to our ');
      const privacyLink = createElement('a', '', 'Privacy Policy');
      privacyLink.href = '/policies/privacy-policy';
      const termsLink = createElement('a', '', 'Terms');
      termsLink.href = '/policies/terms-of-service';
      privacy.append(privacyLink, ' and ', termsLink, '. Reviews are checked before they appear.');
      nativeForm.appendChild(privacy);
    }

    root.classList.add('aykah-trustoo-layout-ready');
    decorateNativeCopy(root);
    return true;
  };

  const decorateForm = (form) => {
    const section = document.querySelector(V2_SELECTOR);
    if (!section) return;
    if (form.dataset.aykahTrustooBranded !== 'true') form.dataset.aykahTrustooBranded = 'true';

    const closeControls = form.querySelectorAll(CLOSE_SELECTOR);
    closeControls.forEach((control) => {
      control.classList.add('aykah-trustoo-close');
      // setAttribute fires a mutation record even when the value is unchanged.
      if (control.getAttribute('role') !== 'button') control.setAttribute('role', 'button');
      if (control.getAttribute('tabindex') !== '0') control.setAttribute('tabindex', '0');
      if (!control.getAttribute('aria-label')) control.setAttribute('aria-label', 'Close');
      ensureIcon(control, 'x', 'aykah-trustoo-close-icon');
    });

    installLayout(form, section);

    form.querySelectorAll('[class*="success" i], [class*="thank" i]').forEach((message) => {
      message.classList.add('aykah-trustoo-success');
      ensureIcon(message, 'checkCircle', 'aykah-trustoo-success-icon');
    });
  };

  let scheduledFrame = 0;
  const decorateForms = () => {
    scheduledFrame = 0;
    if (!document.querySelector(V2_SELECTOR)) return;
    document.querySelectorAll(FORM_SELECTOR).forEach(decorateForm);
  };

  const scheduleDecoration = () => {
    if (scheduledFrame) return;
    scheduledFrame = window.requestAnimationFrame(decorateForms);
  };

  const findOpenForm = () => Array.from(document.querySelectorAll(FORM_SELECTOR))
    .find((candidate) => window.getComputedStyle(candidate).display !== 'none');

  /*
   * Trustoo rewrites the whole form when it validates. Every class and every
   * node this adapter adds is wiped, which leaves the raw app form sitting
   * under our absolute-position CSS - fields stack on their own labels and the
   * layout collapses. The mutation observer below does not always see it, so
   * this checks the marker class while a form is open and puts the skin back.
   */
  /*
   * Trustoo builds its confirmation at submit time and it is in none of the
   * loaded bundles, so there is no class name to hook. Find it by the one thing
   * that is stable - the sentence it prints - then dress it to match the design.
   */
  let lastReviewerName = '';

  const decorateSuccess = () => {
    const mask = document.querySelector(MASK_SELECTOR);
    if (!mask) return;
    // Cheap exits first. This runs on a timer, so it must cost nothing in the
    // normal case where there is no confirmation on screen.
    if (mask.dataset.aykahSuccessDone === 'true') return;
    if (window.getComputedStyle(mask).display === 'none') return;
    if (findOpenForm()) return;

    // Leaves only, found by walking children rather than asking each node to
    // search its own subtree - the latter is quadratic and froze the page.
    const leaves = [];
    const walk = (node) => {
      const kids = node.children;
      if (!kids.length) { leaves.push(node); return; }
      for (let i = 0; i < kids.length; i += 1) walk(kids[i]);
    };
    walk(mask);
    const deepest = (test) => {
      for (let i = leaves.length - 1; i >= 0; i -= 1) {
        if (test(leaves[i].textContent || '')) return leaves[i];
      }
      return null;
    };

    const sent = deepest((text) => /has been sent|has been submitted|received your review/i.test(text));
    const thanks = deepest((text) => /^\s*thank/i.test(text));
    if (!sent && !thanks) return;

    let panel = sent || thanks;
    while (panel.parentElement && panel.parentElement !== mask) panel = panel.parentElement;
    panel.classList.add('aykah-trustoo-success-panel');

    if (!panel.querySelector('.aykah-trustoo-success-ring')) {
      const ring = createElement('span', 'aykah-trustoo-success-ring');
      ring.appendChild(makeIcon('check', 'aykah-trustoo-success-check'));
      panel.insertAdjacentElement('afterbegin', ring);
    }

    if (thanks) {
      thanks.classList.add('aykah-trustoo-success-heading');
      const wanted = lastReviewerName ? `Thanks, ${lastReviewerName}` : 'Thanks';
      if (thanks.textContent !== wanted) thanks.textContent = wanted;
    }

    if (sent) {
      sent.classList.add('aykah-trustoo-success-body');
      const wanted = 'Your review is with our team. Most reviews appear within 24 hours, and we will email you the moment yours is live.';
      if (sent.textContent !== wanted) sent.textContent = wanted;
    }

    const controls = Array.from(panel.querySelectorAll('button, a'));
    const closer = controls.find((node) => /close/i.test(node.className || '') || /close/i.test(node.getAttribute('aria-label') || ''));
    if (closer) closer.classList.add('aykah-trustoo-success-close');
    const action = controls.find((node) => node !== closer && (node.textContent || '').trim());
    if (action) {
      action.classList.add('aykah-trustoo-success-action');
      if (action.textContent.trim() !== 'Back to the product') action.textContent = 'Back to the product';
      if (!panel.querySelector('.aykah-trustoo-success-secondary')) {
        const again = createElement('button', 'aykah-trustoo-success-secondary', 'Write another review');
        again.type = 'button';
        again.addEventListener('click', () => {
          action.click();
          window.setTimeout(() => document.querySelector('[data-pr-write-review]')?.click(), 260);
        });
        action.insertAdjacentElement('afterend', again);
      }
    }
    if (sent && thanks && action) mask.dataset.aykahSuccessDone = 'true';
  };

  let repairTimer = 0;
  const startRepairWatch = () => {
    if (repairTimer) return;
    repairTimer = window.setInterval(() => {
      if (!document.querySelector(V2_SELECTOR)) return;
      const form = findOpenForm();
      decorateSuccess();
      if (!form || form.classList.contains('aykah-trustoo-layout-ready')) return;
      decorateForm(form);
    }, 250);
  };

  // Always close through Trustoo's own control so its internal state, uploads
  // and scroll lock unwind the way the app expects.
  const closeOpenForm = () => {
    const form = findOpenForm();
    const control = form?.querySelector('.aykah-trustoo-close') || form?.querySelector(CLOSE_SELECTOR);
    if (!control) return false;
    control.click();
    return true;
  };

  const installDismissal = () => {
    if (document.body.dataset.aykahTrustooDismissBound === 'true') return;
    document.body.dataset.aykahTrustooDismissBound = 'true';

    document.addEventListener('keydown', (event) => {
      if (!document.querySelector(V2_SELECTOR)) return;
      if (event.key === 'Escape') {
        if (closeOpenForm()) event.preventDefault();
        return;
      }
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const control = event.target?.closest?.('.aykah-trustoo-close');
      if (!control) return;
      event.preventDefault();
      control.click();
    });

    // Trustoo's mask is the parent of the panel, so a click that lands on the
    // mask itself is a click outside the panel.
    document.addEventListener('click', (event) => {
      if (!document.querySelector(V2_SELECTOR)) return;
      if (!event.target?.matches?.(MASK_SELECTOR)) return;
      closeOpenForm();
    });
  };

  const initialize = () => {
    scheduleDecoration();
    if (!document.body) return;
    installDismissal();
    // Repair in the same frame the skin is stripped. Waiting for the next
    // animation frame is long enough to see the unstyled form flash.
    const observer = new MutationObserver(() => {
      const open = findOpenForm();
      if (open && !open.classList.contains('aykah-trustoo-layout-ready')) {
        decorateForm(open);
        return;
      }
      decorateSuccess();
      scheduleDecoration();
    });
    // Decoration is idempotent, so watching class changes settles rather than loops.
    // childList only. Watching every class change on the page meant this ran on
    // each frame of the marquees and scroll rails, which is far too hot.
    observer.observe(document.body, { childList: true, subtree: true });
    startRepairWatch();
    document.addEventListener('shopify:section:load', scheduleDecoration);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
